import { IWebContentMessageEvent, IWebContentRequestMessage } from '../messages.interface';
import { IMessageHandler, IMessageProcessor } from './messaging-infra.interface';
import { deepCopy } from '../utils';
import { WebContentSdkService } from '../web-content-sdk';

/**
 * MessageProcessor for processing the message requests which involves :
 * Routing message requests to proper handler
 * Posting response back to the app
 */
export class BaseMessageProcessor implements IMessageProcessor {
  public messageHandlers: IMessageHandler[] = [];

  /**
   * Registers different available handlers
   */
  constructor(protected appSdkHost: WebContentSdkService) {}

  /**
   * Responsible for processing of message requests from MetaOS App
   */
  public async process(event: IWebContentMessageEvent): Promise<boolean> {
    try {
      const message: IWebContentRequestMessage = event.data;

      let requestArgs: unknown[] | undefined = message.args;
      // We need to prepend it so we can guarantee the index.
      // We need to make a deep copy of the args before altering it since the message event is reused across listeners.
      requestArgs = message.args && Array.isArray(message.args) ? message.args.map((arg) => deepCopy(arg)) : [];
      requestArgs.unshift(event);

      const result: unknown = await this.callMessageHandler(message.func, requestArgs, message.timestamp);

      if (result) {
        this.processHandlerResultHelper(message, result as object);
        return Promise.resolve(true);
      }
    } catch (err) {
      console.warn(`Error in processing request : ${event.data?.func} with error ${err}`);
    }
    return Promise.resolve(false);
  }

  private callMessageHandler(message: string, args: unknown[], clientTimestamp?: number): Promise<unknown> {
    for (const handler of this.messageHandlers) {
      if (handler.canHandleMessage(message)) {
        return handler.handleMessage(message, args, clientTimestamp);
      }
    }

    return Promise.reject('Unhandled message');
  }

  /**
   * Responsible for posting the response back to the App
   */
  private processHandlerResultHelper(message: IWebContentRequestMessage, response: object | object[]): void {
    const messageId: number | undefined = message.id;
    const responseArgs = Array.isArray(response) ? response : [response];

    this.appSdkHost.postResponseToApp(messageId, responseArgs);
  }

  public cleanup(): void {
    this.messageHandlers.forEach((mh) => mh.cleanup());
  }
}
